import { PropertyKind } from "~/shared/enums";
import type { ColDef } from "~/shared/types";
import CheckboxImage from "~/tooltip-images/CheckboxImage.png";
import DatesImage from "~/tooltip-images/DatesImage.png";
import MultiselectImage from "~/tooltip-images/MultiselectImage.png";
import NumberImage from "~/tooltip-images/NumberImage.png";
import SelectImage from "~/tooltip-images/SelectImage.png";
import StatusImage from "~/tooltip-images/StatusImage.png";
import TextImage from "~/tooltip-images/TextImage.png";
import TimeTrackingImage from "~/tooltip-images/TimeTrackingImage.png";
import UserImage from "~/tooltip-images/UserImage.png";

export const DEFAULT_DATES_SINGLE_TITLE = "Due date";

export const DEFAULT_PROPERTY_DESCRIPTIONS = new Map([
  [
    PropertyKind.DEFAULT_KIND,
    "Set the work item type to organize work at different levels of detail, whether it's a task, subtask, project, milestone, or a custom type you define.",
  ],
  [
    PropertyKind.DEFAULT_STATUS,
    "Track the current state of the task, and update the progress as work moves forward so everyone has clear visibility.",
  ],
  [
    PropertyKind.DEFAULT_ASSIGNEES,
    "Select the person responsible for completing this task. The assignee will own the work, receive updates, and be accountable for progress. Leave unassigned if the task isn't yet allocated.",
  ],
  [
    PropertyKind.DEFAULT_TAGS,
    "Add labels to group and organize tasks. Tags make it easier to filter, search, and highlight related work across your workspace.",
  ],
  [
    PropertyKind.DEFAULT_PRIORITY,
    "Indicate the level of importance or urgency for any task. Priority helps teams decide what to focus on first.",
  ],
  [
    PropertyKind.DEFAULT_DATES,
    "Add a due date and, optionally, a start date. Dates keep work on schedule and help teams manage deadlines.",
  ],
  [
    PropertyKind.DEFAULT_SIZE,
    "Estimate the level of effort or complexity of the task. Size helps with planning, forecasting, and balancing workload.",
  ],
  [
    PropertyKind.DEFAULT_TIME_TRACKING,
    "Log and monitor time spent working on the task. Time tracking provides visibility into effort, productivity, and billing.",
  ],
  [
    PropertyKind.DEFAULT_CREATED_AT,
    "The date when the task was first created. This is recorded automatically and helps track task creation history.",
  ],
  [
    PropertyKind.DEFAULT_CREATED_BY,
    "The person who originally created the task. This is recorded automatically and helps track task ownership history.",
  ],
  [
    PropertyKind.DEFAULT_UPDATED_AT,
    "The most recent date when any change was made to the task. This is updated automatically and provides visibility into activity history.",
  ],
  [
    PropertyKind.DEFAULT_UPDATED_BY,
    "The person who most recently made changes to the task. This is updated automatically and provides visibility into activity history.",
  ],
  [
    PropertyKind.DEFAULT_COMPLETED_AT,
    "The date when the task was completed. This is recorded automatically when the task status changes to a completed state, and cleared if the task is reopened.",
  ],
  [
    PropertyKind.DEFAULT_REVIEWERS,
    "The person or people responsible for reviewing this task. Reviewers are notified of changes and can be used to track review workflows.",
  ],
]);

export type PropertyKindDescription = {
  image: string;
  description: string;
};

export const PROPERTY_KIND_DESCRIPTIONS = new Map<PropertyKind, PropertyKindDescription>([
  [
    PropertyKind.SELECT,
    {
      image: SelectImage,
      description:
        "Pick one option from a predefined list. Great for categories like department, customer segment, or project phase.",
    },
  ],
  [
    PropertyKind.MULTISELECT,
    {
      image: MultiselectImage,
      description: "Choose multiple options from a list. Perfect for tagging items with several attributes or themes.",
    },
  ],
  [
    PropertyKind.STATUS,
    {
      image: StatusImage,
      description:
        "Track progress with clear stages. Use the default Status property for task progress, and create a custom one only if you need additional workflows.",
    },
  ],
  [
    PropertyKind.USER,
    {
      image: UserImage,
      description:
        "Assign responsibility by linking teammates. Use the default Assignee property for task ownership, and add a custom one if you need to track other roles.",
    },
  ],
  [
    PropertyKind.DATES,
    {
      image: DatesImage,
      description:
        "Attach calendar dates to tasks. Use the default Date property for task deadlines, and create custom Date properties for other time-based needs.",
    },
  ],
  [
    PropertyKind.TIME_TRACKING,
    {
      image: TimeTrackingImage,
      description:
        "Log the time spent on tasks. Use the default Time tracking property to track effort, and create a custom one if you need extra tracking categories.",
    },
  ],
  [PropertyKind.TEXT, { image: TextImage, description: "Capture any information in freeform text." }],
  [
    PropertyKind.NUMBER,
    {
      image: NumberImage,
      description: "Record any numeric values associated with the task, such as progress, budget, or quantities.",
    },
  ],
  [
    PropertyKind.CHECKBOX,
    {
      image: CheckboxImage,
      description: "A simple yes or no field. Use it for flags like Tested, Reviewed, Published, or Approved.",
    },
  ],
]);

export const AI_PROPERTY_PSEUDO_DUID = "@ai";

export const TRASH_PROPERTY_PSEUDO_DUID = "@trash";
export const QUERY_PROPERTY_PSEUDO_DUID = "@query";
export const RECENT_PROPERTY_PSEUDO_DUID = "@recent";

export const DELETED_PROPERTY_PSEUDO_DUID = "@deleted";
export const SPACE_PROPERTY_PSEUDO_DUID = "@space";
export const DARTBOARD_KIND_PROPERTY_PSEUDO_DUID = "@dartboard_kind";
export const TITLE_PROPERTY_PSEUDO_DUID = "@title";
export const DESCRIPTION_PROPERTY_PSEUDO_DUID = "@description";
export const STATUS_KIND_PROPERTY_PSEUDO_DUID = "@status_kind";
export const COMMENTS_PROPERTY_PSEUDO_DUID = "@comments";
export const ATTACHMENTS_PROPERTY_PSEUDO_DUID = "@attachments";
export const LINKS_PROPERTY_PSEUDO_DUID = "@links";
export const RELATIONSHIPS_PROPERTY_PSEUDO_DUID = "@relationships";
export const DOCS_PROPERTY_PSEUDO_DUID = "@docs";
export const SUBSCRIBERS_PROPERTY_PSEUDO_DUID = "@subscriberDuids";
export const REVIEWERS_PROPERTY_PSEUDO_DUID = "@reviewerDuids";
export const REMIND_AT_PROPERTY_PSEUDO_DUID = "@remindAt";
export const RECURRENCE_PROPERTY_PSEUDO_DUID = "@recurrence";
export const OTHER_PROPERTY_PSEUDO_DUID = "@other";

export const DUID_PROPERTY_PSEUDO_DUID = "duid";
export const ORDER_PROPERTY_PSEUDO_DUID = "order";
export const CONFIG_PROPERTY_PSEUDO_DUID = "config";

export const DEFAULT_COLUMN_WIDTH_LIMITS = {
  minWidth: 100,
  maxWidth: 400,
};
export const TITLE_COLUMN_MIN_WIDTH = 400;
export const TITLE_COLUMN_MAX_MIN_WIDTH = 600;

export const STATUS_COLUMN_MOBILE_WIDTH = 20;
export const ASSIGNEES_COLUMN_MOBILE_WIDTH = 58;

export const DUID_COLUMN_DEF: ColDef = {
  field: DUID_PROPERTY_PSEUDO_DUID,
  headerName: "DUID",
  suppressMovable: true,
  sortable: true,
};

export const ORDER_COLUMN_DEF: ColDef = {
  field: ORDER_PROPERTY_PSEUDO_DUID,
  headerName: "Order",
  suppressMovable: true,
  sortable: true,
};

export const CONFIG_COLUMN_DEF: ColDef = {
  field: CONFIG_PROPERTY_PSEUDO_DUID,
  suppressMovable: true,
  lockPosition: "right",
  resizable: false,
  sortable: false,
  valueGetter: () => "",
};

export const PROPERTY_KIND_TO_DEFAULT_WIDTH_MAP = new Map([
  [DUID_PROPERTY_PSEUDO_DUID, 100],
  [ORDER_PROPERTY_PSEUDO_DUID, 100],
  [PropertyKind.DEFAULT_KIND, 100],
  [PropertyKind.DEFAULT_TITLE, 500],
  [PropertyKind.DEFAULT_DARTBOARD, 100],
  [PropertyKind.DEFAULT_STATUS, 100],
  [PropertyKind.DEFAULT_ASSIGNEES, 120],
  [PropertyKind.DEFAULT_DATES, 180],
  [PropertyKind.DEFAULT_PRIORITY, 100],
  [PropertyKind.DEFAULT_TAGS, 160],
  [PropertyKind.DEFAULT_SIZE, 100],
  [PropertyKind.DEFAULT_TIME_TRACKING, 100],
  [PropertyKind.DEFAULT_REVIEWERS, 120],
  [PropertyKind.DEFAULT_CREATED_AT, 100],
  [PropertyKind.DEFAULT_CREATED_BY, 100],
  [PropertyKind.DEFAULT_UPDATED_AT, 100],
  [PropertyKind.DEFAULT_UPDATED_BY, 100],
  [PropertyKind.DEFAULT_COMPLETED_AT, 100],
  [PropertyKind.SELECT, 160],
  [PropertyKind.MULTISELECT, 160],
  [PropertyKind.STATUS, 100],
  [PropertyKind.USER, 100],
  [PropertyKind.DATES, 180],
  [PropertyKind.TIME_TRACKING, 100],
  [PropertyKind.TEXT, 160],
  [PropertyKind.NUMBER, 100],
  [PropertyKind.CHECKBOX, 100],
]);

export const NO_DROPDOWN_PROPERTY_KIND_SET = new Set([
  PropertyKind.DEFAULT_TITLE,
  PropertyKind.DEFAULT_DESCRIPTION,
  PropertyKind.DEFAULT_ATTACHMENTS,
  PropertyKind.CHECKBOX,
  PropertyKind.NUMBER,
  PropertyKind.TEXT,
]);

export const NO_SETTINGS_PROPERTY_KIND_SET = new Set([
  PropertyKind.DEFAULT_TITLE,
  PropertyKind.DEFAULT_DESCRIPTION,
  PropertyKind.DEFAULT_DARTBOARD,
  PropertyKind.DEFAULT_ATTACHMENTS,
]);

export const NOT_SHOWN_PROPERTY_KINDS = new Set([
  PropertyKind.DEFAULT_DARTBOARD,
  PropertyKind.DEFAULT_DESCRIPTION,
  PropertyKind.DEFAULT_ATTACHMENTS,
]);

export const USER_PROPERTY_KINDS = new Set([
  PropertyKind.DEFAULT_ASSIGNEES,
  PropertyKind.DEFAULT_REVIEWERS,
  PropertyKind.DEFAULT_CREATED_BY,
  PropertyKind.DEFAULT_UPDATED_BY,
  PropertyKind.USER,
]);
